import React, { useEffect, useRef, useState } from 'react';
import { Button, Image } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

import User from './User';
import Group from './Group';
import Departament from './Department';
import Parameter from './Parameter';

import IUserGroup from '../interfaces/IUserGroup';
import IUserDepartament from '../interfaces/IUserDepartament';
import IUserSupervisor from '../interfaces/IUserSupervisor';

import { useToast } from '../hooks/toast';

import api from '../services/apiClient';

import AddUserIcon from '../assets/icon/addUser.svg';
import ConversaGrupoIcon from '../assets/icon/conversaGrupo.svg';
import DepartmentIcon from '../assets/icon/department.svg';
import ParameterIcon from '../assets/icon/opcoes.svg';

enum SettingsMenu {
  NONE,
  USER,
  GROUP,
  DEPARTAMENT,
  PARAMETRO,
}

interface DataUser {
  usuario: string | null;
  seqUsuario: number;
}

const Settings: React.FC<any> = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [settingsMenu, setSettingsMenu] = useState<SettingsMenu>(
    SettingsMenu.NONE
  );

  const { addToast } = useToast();
  const [userSelected, setUserSelected] = useState<DataUser>({} as DataUser);
  const [administrador, setAdministrador] = useState(false);
  const [userGroups, setUserGroups] = useState<IUserGroup[]>([]);
  const [userDepartaments, setUserDepartaments] = useState<IUserDepartament[]>(
    []
  );
  const [userSupervisor, setUserSupervisor] = useState<IUserSupervisor[]>([]);
  const refButtonSalvar = useRef<HTMLButtonElement>(null);
  const [showMenu, setShowMenu] = useState(true);

  useEffect(() => {
    setAdministrador(localStorage.getItem('@LiveChat:administrador') === 'S');
  }, []);

  useEffect(() => {
    const maximizeLog = searchParams.get('maximizeLog') === 'true';
    setShowMenu(!maximizeLog);
  }, [searchParams]);

  const handlerSaveUserGroup = async (): Promise<void> => {
    try {
      if (refButtonSalvar.current) {
        refButtonSalvar.current.disabled = true;
      }

      const token = localStorage.getItem('@LiveChat:token');
      const response = await api.post('/GrupoUsuario', userGroups, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        addToast({
          title: 'Sucesso',
          description: 'Permissão de grupo gravado com sucesso.',
          type: 'success',
        });
      } else {
        addToast({
          title: 'Erro',
          description: 'Erro ao gravar permissão de grupo.',
          type: 'error',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    } finally {
      if (refButtonSalvar.current) {
        refButtonSalvar.current.disabled = false;
      }
    }
  };

  const handlerSaveUserDepartament = async (): Promise<void> => {
    try {
      if (refButtonSalvar.current) {
        refButtonSalvar.current.disabled = true;
      }

      const token = localStorage.getItem('@LiveChat:token');
      const response = await api.post(
        '/DepartamentoUsuario',
        userDepartaments,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        addToast({
          title: 'Sucesso',
          description: 'Permissão de departamento gravado com sucesso.',
          type: 'success',
        });
      } else {
        addToast({
          title: 'Erro',
          description: 'Erro ao gravar permissão de departamento.',
          type: 'error',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    } finally {
      if (refButtonSalvar.current) {
        refButtonSalvar.current.disabled = false;
      }
    }
  };

  const handlerSaveUserSupervisor = async (): Promise<void> => {
    try {
      if (refButtonSalvar.current) {
        refButtonSalvar.current.disabled = true;
      }

      const token = localStorage.getItem('@LiveChat:token');
      const response = await api.post('/Supervisor', userSupervisor, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        addToast({
          title: 'Sucesso',
          description: 'Dados do supervisor gravados com sucesso.',
          type: 'success',
        });
      } else {
        addToast({
          title: 'Erro',
          description: 'Erro ao gravar dados do supervisor.',
          type: 'error',
        });
      }
    } catch (err: any) {
      addToast({
        title: 'Erro',
        description: err.message,
        type: 'error',
      });
    } finally {
      if (refButtonSalvar.current) {
        refButtonSalvar.current.disabled = false;
      }
    }
  };

  const handlerSave = (): void => {
    if (administrador && userSelected.seqUsuario !== undefined) {
      handlerSaveUserGroup();
      handlerSaveUserDepartament();
      handlerSaveUserSupervisor();
    }
  };

  function showPageSelected(): JSX.Element {
    switch (settingsMenu) {
      case SettingsMenu.USER:
        return (
          <User setUserSelected={setUserSelected} handlerSave={handlerSave} />
        );
      case SettingsMenu.GROUP:
        return <Group />;
      case SettingsMenu.DEPARTAMENT:
        return <Departament />;
      case SettingsMenu.PARAMETRO:
        return <Parameter />;
      default:
        return <div> </div>;
    }
  }

  return (
    <div className="d-flex flex-column ms-3">
      <span style={{ fontSize: '2.5rem', fontWeight: '600' }}>
        Configurações
      </span>
      <div className="d-flex w-100 mt-3">
        {showMenu && (
          <section className="d-flex flex-column">
            <Button
              variant="primary"
              className="p-0 m-0"
              style={{
                height: '6.25rem',
                width: '17.5rem',
                backgroundColor:
                  settingsMenu === SettingsMenu.USER ? '#0052B2' : '',
              }}
              onClick={() => setSettingsMenu(SettingsMenu.USER)}
            >
              <div className="d-flex align-items-center justify-content-center h-100 ps-3 pe-3">
                <Image
                  style={{ height: '2.5rem', width: '2.5rem' }}
                  src={AddUserIcon}
                  alt="Adicionar usuário"
                />
                <span
                  className="text-white ps-5 pe-4"
                  style={{ fontSize: '1.25rem', fontWeight: '600' }}
                >
                  Cadastro de Usuário
                </span>
              </div>
            </Button>
            {administrador && (
              <>
                <Button
                  className="p-0 m-0 mt-3"
                  style={{
                    height: '6.25rem',
                    width: '17.5rem',
                    backgroundColor:
                      settingsMenu === SettingsMenu.GROUP ? '#0052B2' : '',
                  }}
                  onClick={() => setSettingsMenu(SettingsMenu.GROUP)}
                >
                  <div className="d-flex align-items-center justify-content-center h-100 ps-3 pe-2">
                    <Image
                      style={{ height: '3.12rem', width: '3.12rem' }}
                      src={ConversaGrupoIcon}
                      alt="Adicionar grupo"
                    />
                    <span
                      className="text-white ps-5 pe-4"
                      style={{ fontSize: '1.25rem', fontWeight: '600' }}
                    >
                      Cadastro de Grupo
                    </span>
                  </div>
                </Button>

                <Button
                  className="p-0 m-0 mt-3"
                  style={{
                    height: '6.25rem',
                    width: '17.5rem',
                    backgroundColor:
                      settingsMenu === SettingsMenu.DEPARTAMENT
                        ? '#0052B2'
                        : '',
                  }}
                  onClick={() => setSettingsMenu(SettingsMenu.DEPARTAMENT)}
                >
                  <div className="d-flex align-items-center justify-content-center h-100 ps-5 pe-3">
                    <Image
                      style={{ height: '2.5rem', width: '2.5rem' }}
                      src={DepartmentIcon}
                      alt="Adicionar departamento"
                    />
                    <span
                      className="text-white ps-5 pe-4"
                      style={{ fontSize: '1.25rem', fontWeight: '600' }}
                    >
                      Cadastro de Departamento
                    </span>
                  </div>
                </Button>
                <Button
                  className="p-0 m-0 mt-3"
                  style={{
                    height: '6.25rem',
                    width: '17.5rem',
                    backgroundColor:
                      settingsMenu === SettingsMenu.PARAMETRO ? '#0052B2' : '',
                  }}
                  onClick={() => setSettingsMenu(SettingsMenu.PARAMETRO)}
                >
                  <div className="d-flex align-items-center justify-content-center h-100 ps-3 pe-3">
                    <Image
                      style={{ height: '2.5rem', width: '2.5rem' }}
                      src={ParameterIcon}
                      alt="Parâmetro"
                    />
                    <span
                      className="text-white ps-5 pe-4"
                      style={{ fontSize: '1.25rem', fontWeight: '600' }}
                    >
                      Parâmetro
                    </span>
                  </div>
                </Button>
              </>
            )}
          </section>
        )}

        {/* Configurações */}
        <section
          className={`w-100 me-2 overflow-auto ${showMenu ? 'ms-4' : ''} `}
        >
          <div style={{ height: 'Calc(100vh - 15rem)', width: '100%' }}>
            {showPageSelected()}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Settings;
